'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getClient } from '@/lib/supabase/client';
import { useNotificationCenter } from '@/hooks/useNotificationCenter';
import { useFeaturedRewardToast } from '@/hooks/useFeaturedRewardToast';

const NotificationCenterContext = createContext(null);

export default function NotificationCenterProvider({ children }) {
  const supabase = useMemo(() => getClient(), []);
  const [userId, setUserId] = useState(null);
  const [open, setOpen] = useState(false);
  const center = useNotificationCenter({
    enabled: true,
    authenticated: Boolean(userId),
    userId,
    open,
  });

  useFeaturedRewardToast(center.featuredRewards, center.markRead);

  useEffect(() => {
    let active = true;

    const syncSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (active) setUserId(data.session?.user?.id || null);
    };

    void syncSession();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setUserId(session?.user?.id || null);
      if (!session?.user) setOpen(false);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  const value = useMemo(() => ({
    ...center,
    open,
    setOpen,
  }), [center, open]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function useSharedNotificationCenter() {
  const center = useContext(NotificationCenterContext);
  if (!center) {
    throw new Error('useSharedNotificationCenter must be used within NotificationCenterProvider');
  }
  return center;
}
